import { Modal } from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import { loadingReducer, loadingReducerInitialState } from '../../reducer/loadingReducer';
import {
  TimetableBlockInput,
  useCreateTimetableBlockMutation,
  useDeleteTimetableBlockMutation,
  useUpdateTimetableBlockMutation,
} from '../../types/planung-graphql-client-defs';
import { isFirstBeforeSecond } from '../../utils/dateCalculations';
import { createTimetableBlocks } from './createTimetableBlocks';
import { TimetableBlockForm } from './Forms/TimetableBlockForm';
import { TimetableBlockFormValues } from './graphql';
import { TIMETABLE_BLOCK_MUTATION_CONTEXT, useTimetableBlocks } from './hooks/useTimetableBlocks';

export type ProcessedTimetableBlockType = {
  uuid: string;
  name: string;
  start: Date;
  end: Date;
  days: number;
  empty: boolean;
  currentActive: boolean;
};

/**
 * Grenze für Start bzw. Ende eines Abschnitts. `isSchoolYearBoundary` unterscheidet, ob die
 * Grenze von einem Nachbarabschnitt kommt (dann muss ein Tag Abstand bleiben) oder vom
 * Schuljahr selbst (dann ist der Tag noch wählbar: der erste Abschnitt darf am Schuljahres-
 * beginn starten, der letzte am Schuljahresende enden).
 */
export type BlockBoundary = {
  date: Date;
  isSchoolYearBoundary: boolean;
};

export const useTimetableBlock = () => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();
  const permissions = usePermissionChecker();

  const [loading, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedBlock, setSelectedBlock] = useState<ProcessedTimetableBlockType | null>(null);

  const openModal = (block: ProcessedTimetableBlockType | null) => {
    setSelectedBlock(block);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedBlock(null);
    setModalOpen(false);
  };

  const [, create] = useCreateTimetableBlockMutation();
  const [, update] = useUpdateTimetableBlockMutation();
  const [, remove] = useDeleteTimetableBlockMutation();

  // Kommen nach Beginn sortiert.
  const { timetableBlocks: loadedBlocks } = useTimetableBlocks(schoolYear?.uuid);
  const timetableBlocks = useMemo(() => loadedBlocks ?? [], [loadedBlocks]);

  const schoolYearStart = schoolYear ? dayjs(schoolYear.start) : dayjs();
  const schoolYearEnd = schoolYear ? dayjs(schoolYear.end) : dayjs().add(1, 'day');

  const processedBlocks = createTimetableBlocks(schoolYearStart, schoolYearEnd, timetableBlocks);

  const getFormData = (uuid: string): TimetableBlockFormValues => {
    const block = timetableBlocks.find((b) => b.uuid === uuid);
    const currentProcessedBlock = processedBlocks.find((b) => b.uuid === uuid);
    const prevBlock = currentProcessedBlock
      ? processedBlocks[processedBlocks.indexOf(currentProcessedBlock) - 1]
      : null;
    const nextBlock = currentProcessedBlock
      ? processedBlocks[processedBlocks.indexOf(currentProcessedBlock) + 1]
      : null;
    return {
      uuid: uuid,
      end: block?.end
        ? new Date(block.end)
        : nextBlock
          ? dayjs(nextBlock?.start).subtract(1, 'day').toDate()
          : dayjs(schoolYearEnd).toDate(),
      start: block?.start
        ? new Date(block.start)
        : prevBlock
          ? dayjs(prevBlock?.end).add(1, 'day').toDate()
          : dayjs(schoolYearStart).toDate(),
      timetables: block?.timetables.map((timetable) => ({ uuid: timetable.uuid, name: timetable.name })) ?? [],
    };
  };

  const getLatestStart = (uuid: string): BlockBoundary => {
    const currentBlock = processedBlocks.find((b) => b.uuid === uuid);
    const prevBlock = currentBlock ? processedBlocks[processedBlocks.indexOf(currentBlock) - 1] : null;
    return prevBlock
      ? { date: dayjs(prevBlock.end).toDate(), isSchoolYearBoundary: false }
      : { date: schoolYearStart.toDate(), isSchoolYearBoundary: true };
  };

  const getEarliestEnd = (uuid: string): BlockBoundary => {
    const currentBlock = processedBlocks.find((b) => b.uuid === uuid);
    const nextBlockNotEmptyBlock = currentBlock
      ? processedBlocks.find((b) => b.start > currentBlock.start && !b.empty)
      : null;
    return nextBlockNotEmptyBlock
      ? { date: dayjs(nextBlockNotEmptyBlock.start).toDate(), isSchoolYearBoundary: false }
      : { date: schoolYearEnd.toDate(), isSchoolYearBoundary: true };
  };

  // Lokales Kalenderdatum speichern – KEIN .utc(), sonst kippt das Datum in UTC+-Zeitzonen
  // auf den Vortag (z. B. 31.07. -> 30.07.).
  const toInput = (values: TimetableBlockFormValues): TimetableBlockInput => ({
    schoolYearUuid: schoolYear?.uuid ?? '',
    start: dayjs(values.start).format('YYYY-MM-DD'),
    end: dayjs(values.end).format('YYYY-MM-DD'),
    timetableUuids: values.timetables.map((timetable) => timetable.uuid),
  });

  const createTimetableBlock = async (values: TimetableBlockFormValues) => {
    return await create({ input: toInput(values) }, TIMETABLE_BLOCK_MUTATION_CONTEXT);
  };

  /** Ohne Stundenpläne gibt es keinen Zeitraum: dann wird er gelöscht. */
  const updateTimetableBlock = async (values: TimetableBlockFormValues, uuid: string) => {
    if (values.timetables.length === 0) {
      return await deleteTimetableBlock(uuid);
    }
    dispatch({ type: 'SET_LOADING', uuids: [uuid] });
    const result = await update({ uuid, input: toInput(values) }, TIMETABLE_BLOCK_MUTATION_CONTEXT);
    dispatch({ type: 'REMOVE_LOADING', uuids: [uuid] });
    return result;
  };

  const deleteTimetableBlock = async (uuid: string) => {
    dispatch({ type: 'SET_LOADING', uuids: [uuid] });
    const res = await remove({ uuid }, TIMETABLE_BLOCK_MUTATION_CONTEXT);
    dispatch({ type: 'REMOVE_LOADING', uuids: [uuid] });
    return res;
  };

  const TimetableBlockModal = () => (
    <Modal
      title={selectedBlock ? t('timetableBlock.editBlock') : t('timetableBlock.addBlock')}
      isOpen={modalOpen}
      onRequestClose={() => {
        setModalOpen(false);
      }}
    >
      {selectedBlock && (
        <TimetableBlockForm
          block={selectedBlock}
          canEditStart={
            permissions?.isOmniAdmin() || selectedBlock?.empty || !isFirstBeforeSecond(selectedBlock?.start, new Date())
          }
          canEditEnd={
            permissions?.isOmniAdmin() || selectedBlock?.empty || !isFirstBeforeSecond(selectedBlock?.end, new Date())
          }
          canEditTimetables={selectedBlock?.empty || !isFirstBeforeSecond(selectedBlock?.start, new Date())}
          onClose={() => {
            setModalOpen(false);
          }}
        />
      )}
    </Modal>
  );

  return {
    createTimetableBlock,
    updateTimetableBlock,
    deleteTimetableBlock,
    getFormData,
    blocks: timetableBlocks,
    processedBlocks,
    isLoading: loading.some((ls) => timetableBlocks.find((block) => ls.uuid === block.uuid)),
    openModal,
    closeModal,
    TimetableBlockModal,
    selectedBlock,
    getLatestStart,
    getEarliestEnd,
  };
};
